import React, { FC, ReactNode, useEffect, useState } from 'react';
import { Button, Table } from 'antd';
import { InfiniteScroll, SearchInput, Spin } from 'common/components';
import { useUserContext } from 'common/context';
import { showSuccessMessage } from 'common/helpers';
import { Plus } from 'common/svg';
import { DEFAULT_LIST_OFFSET, DEFAULT_PAGINATION_PAGE, LIST_LIMIT_0, LIST_LIMIT_20, PRICE_LIMIT } from 'common/config';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import { IAccount } from 'entities/Account/Account.models';
import { renderPersonalLimitsSettingsRecords } from 'entities/Settings/Settings.helper';
import {
  PersonalLimitChangeModal,
  PersonalLimitDeleteModal,
  PersonalLimitUsersModal,
  UserCard,
  UserEditCard,
} from 'entities/User/components';
import { IUser, IUserListParams } from 'entities/User/User.models';
import { getUserName } from 'entities/User/User.helper';
import {
  createUserWithLimit,
  deleteUserWithLimit,
  getUserById,
  getUserList,
  getUsersWithLimit,
  getUsersWithLimitChunk,
  selectIsUserLoading,
  selectUserList,
  selectUserListWithLimit,
  selectUserListWithLimitCount,
  setUserListWithLimit,
  updateUserWithLimit,
} from 'entities/User/User.slice';

interface IComponentProps {
  header: ReactNode;
  account: IAccount | null;
}

export const SettingsAccountPersonalLimitsTab: FC<IComponentProps> = ({ header, account }) => {
  const dispatch = useAppDispatch();
  // user
  const userList = useAppSelector(selectUserList);
  const isUserLoading = useAppSelector(selectIsUserLoading);
  const userListWithLimit = useAppSelector(selectUserListWithLimit);
  const userListWithLimitCount = useAppSelector(selectUserListWithLimitCount);

  const [fetchLoading, setFetchLoading] = useState<boolean>(true);
  const [scrollLoading, setScrollLoading] = useState<boolean>(false);
  const [page, setPage] = useState<number>(DEFAULT_PAGINATION_PAGE);
  const [openPersonalLimitUsersModal, setOpenPersonalLimitUsersModal] = useState<boolean>(false);
  const [openPersonalLimitChangeModal, setOpenPersonalLimitChangeModal] = useState<boolean>(false);
  const [openPersonalLimitDeleteModal, setOpenPersonalLimitDeleteModal] = useState<boolean>(false);
  const [user, setUser] = useState<IUser | null>(null);
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [params, setParams] = useState<IUserListParams>({
    accountId: account?.id,
    search: '',
    hasLimit: true,
    limit: LIST_LIMIT_20,
    offset: DEFAULT_LIST_OFFSET,
  });
  const { setOpenUserCard } = useUserContext();

  useEffect(() => {
    const fetch = async () => {
      dispatch(setUserListWithLimit([]));
      await dispatch(getUsersWithLimit(params));
      setFetchLoading(false);
    };

    fetch();
  }, []);

  const onSearch = async (value?: string) => {
    const newParams = {
      ...params,
      search: value,
    };

    setParams(newParams);
    await dispatch(getUsersWithLimit(newParams));
  };

  const onCreatePersonalLimitClick = () => {
    setIsEdit(false);
    dispatch(
      getUserList({
        accountId: account?.id,
        hasLimit: false,
        limit: LIST_LIMIT_0,
        onSuccess: () => setOpenPersonalLimitUsersModal(true),
      }),
    );
  };

  const onSelectUser = (record: IUser, onSuccess: () => void) => {
    setUser(record);
    onSuccess();
    setOpenPersonalLimitChangeModal(true);
  };

  const onPersonalLimitCreate = (value: number, onSuccess: () => void) => {
    if (!user) {
      return;
    }

    const limit = Number((user.spentLimit + value).toFixed(2));

    dispatch(
      createUserWithLimit({
        id: user.id,
        limit: limit > PRICE_LIMIT ? PRICE_LIMIT : limit,
        onSuccess: () => {
          const userName = getUserName.fullName(user.lastName, user.firstName, user.patronymic);

          onSuccess();
          showSuccessMessage(`Персональный лимит сотрудника ${userName} установлен.`);
        },
      }),
    );
  };

  const onEditPersonalLimitClick = (record: IUser) => {
    setIsEdit(true);
    setUser(record);
    setOpenPersonalLimitChangeModal(true);
  };

  const onPersonalLimitUpdate = (value: number, onSuccess: () => void) => {
    if (!user) {
      return;
    }

    let limit = Number((user.spentLimit + value).toFixed(2));

    if (limit > PRICE_LIMIT) {
      limit = PRICE_LIMIT;
    }

    dispatch(
      updateUserWithLimit({
        id: user.id,
        limit,
        onSuccess: () => {
          const userName = getUserName.fullName(user.lastName, user.firstName, user.patronymic);

          onSuccess();
          showSuccessMessage(`Персональный лимит сотрудника ${userName} обновлен.`);
        },
      }),
    );
  };

  const onDeletePersonalLimitClick = (record: IUser) => {
    setUser(record);
    setOpenPersonalLimitDeleteModal(true);
  };

  const onPersonalLimitDelete = () => {
    if (!user) {
      return;
    }

    dispatch(
      deleteUserWithLimit({
        id: user.id,
        limit: null,
        onSuccess: () => setOpenPersonalLimitDeleteModal(false),
      }),
    );
  };

  return (
    <>
      <InfiniteScroll
        loading={scrollLoading}
        hasMore={!isUserLoading && userListWithLimit.length < userListWithLimitCount}
        loader={<Spin />}
        scrollToTopTrigger={[params]}
        loadMore={async () => {
          setScrollLoading(true);
          setPage(page + 1);
          await dispatch(
            getUsersWithLimitChunk({
              ...params,
              offset: LIST_LIMIT_20 * page,
            }),
          );
          setScrollLoading(false);
        }}
      >
        {header}

        <div className="mb-9 ff-regular fs-xs color-dark-grey lineH-normal" style={{ maxWidth: 800 }}>
          Вы можете установить лимиты на закупку для отдельных пользователей. Установленный лимит — общий на все подразделения, в
          которых состоит пользователь. Изменить или снять лимиты можно в любой момент.
        </div>

        <div className="mb-6 flex align-center">
          <div className="mr-4">
            <Button
              className="button-sm primary"
              icon={<Plus className="icon-plus-white" />}
              onClick={onCreatePersonalLimitClick}
            >
              Установить новый
            </Button>
          </div>

          <SearchInput auto placeholder="Введите имя, фамилию или e-mail" onSearch={onSearch} />
        </div>

        <Table
          className={`mb-19 table-hover table-row-middle ${!userListWithLimit.length && 'table-empty'}`}
          showHeader={false}
          pagination={false}
          size="middle"
          dataSource={userListWithLimit.map((user) => ({ ...user, key: user.id }))}
          columns={renderPersonalLimitsSettingsRecords(onEditPersonalLimitClick, onDeletePersonalLimitClick)}
          locale={{
            emptyText: fetchLoading ? <Spin /> : <div className="ff-regular fs-m color-dark-grey">Список пользователей пуст</div>,
          }}
          loading={isUserLoading}
          onRow={({ id }) => ({
            onClick: () => {
              dispatch(
                getUserById({
                  id,
                  onSuccess: () => {
                    setOpenUserCard(true);
                  },
                }),
              );
            },
          })}
        />
      </InfiniteScroll>

      <UserCard />

      <UserEditCard />

      <PersonalLimitUsersModal
        open={openPersonalLimitUsersModal}
        loading={isUserLoading}
        userList={userList}
        onCancel={() => setOpenPersonalLimitUsersModal(false)}
        onSubmit={onSelectUser}
      />

      <PersonalLimitChangeModal
        open={openPersonalLimitChangeModal}
        loading={isUserLoading}
        user={user}
        onCancel={() => setOpenPersonalLimitChangeModal(false)}
        onSubmit={isEdit ? onPersonalLimitUpdate : onPersonalLimitCreate}
      />

      <PersonalLimitDeleteModal
        open={openPersonalLimitDeleteModal}
        user={user}
        loading={isUserLoading}
        onCancel={() => setOpenPersonalLimitDeleteModal(false)}
        onAccept={onPersonalLimitDelete}
      />
    </>
  );
};
